import React, { useState, useEffect } from "react";
import config from "../../../config";
import "./AdminFundraiserTabs.css"; // Ensure this contains border and layout styles for consistency
import { useNavigate } from "react-router-dom";
import { TeamAdminDivisionResponse } from "../../../Interfaces/Division";
import SingleAdminFundraiserComponentDisplay from "./SingleAdminFundraiserDisplay";
import FundraiserCodeDisplay from "../FundraiserCodeDisplay";
import apiClient from "../../../services/apiClient";
import axios from "axios";

const BASE_API = config.API_BASE_URL;

interface DivisionTab {
  label: string; // will be the secondary division name
  primary_division: string;
  primary_id: number;
  secondary_id: number;
}

interface TeamAdminFundraiserTabProps {
  accountID: number;
  fundraiserID: number;
}

export const TeamAdminFundraiserTabs: React.FC<TeamAdminFundraiserTabProps> = ({
  accountID,
  fundraiserID,
}) => {
  const [activeTab, setActiveTab] = useState<DivisionTab | null>(null);
  const [divisionsInfo, setDivisionsInfo] =
    useState<TeamAdminDivisionResponse>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fundName, setFundName] = useState("");
  const navigate = useNavigate();
  const [fundraiserCode, setFundraiserCode] = useState("");

  useEffect(() => {
    const fetchGeneralUserInfo = async (id: number) => {
      try {
        const response = await apiClient.get(
          `${BASE_API}dashboards/teamAdminDivisions/`,
          {
            params: {
              participant_account_id: accountID,
              fundraiser_id: fundraiserID,
            },
          }
        );
        setDivisionsInfo({
          divisions: response.data,
        });
      } catch (error) {
        console.error("Error fetching dashboard data", error);
        setError("Failed to fetch dashboard data");
      } finally {
        // setLoading(false);
      }
    };

    fetchGeneralUserInfo(accountID);
  }, [accountID]);

  useEffect(() => {
    const fetchFundName = async (id: number) => {
      try {
        const response = await axios.get(
          `${BASE_API}fundraiser/${fundraiserID}/detailed`
        );
        setFundName(response.data.fundraiser_name);
        setFundraiserCode(response.data.fundraiser_code);
      } catch (error) {
        console.error("Error fetching fundraiser data", error);
        setError("Failed to fetch fundraiser data");
      }
    };

    fetchFundName(fundraiserID);
  }, [fundraiserID]);

  useEffect(() => {
    if (divisionsInfo) {
      setLoading(true);
      const data = divisionsInfo;
      // Setting the default tab to be the first available tab
      if (data.divisions.length > 0) {
        // should be true if we're able to access team admin page but just in case
        setActiveTab({
          label: data.divisions[0].secondary_division,
          primary_division: data.divisions[0].primary_division,
          primary_id: data.divisions[0].primary_id,
          secondary_id: data.divisions[0].secondary_id,
        });
      }
      setLoading(false);
    }
  }, [divisionsInfo]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const handleTabClick = (tab: DivisionTab) => {
    setActiveTab(tab);
  };

  const tabs: DivisionTab[] = divisionsInfo
    ? [
        ...divisionsInfo.divisions.map((division) => ({
          label: division.secondary_division,
          primary_division: division.primary_division,
          primary_id: division.primary_id,
          secondary_id: division.secondary_id,
        })),
      ]
    : [];

  return (
    <div className="team-admin-fundraiser-tab-display-container">
      {tabs.length === 0 ? (
        <div className="team-admin-fundraiser-tab-display-no-fundraisers-container">
          <p>
            No fundraisers found. Do you have a group that needs fundraising?
          </p>
          <div
            className="team-admin-fundraiser-tab-display-clickable-div"
            onClick={() => {
              navigate("/");
            }}
          >
            Learn about Starting a Fundraiser
          </div>
          <div
            className="team-admin-fundraiser-tab-display-clickable-div"
            onClick={() => {
              navigate("/");
            }}
          >
            Learn about Joining a Fundraiser
          </div>
        </div>
      ) : (
        <div>
          {fundraiserCode && <FundraiserCodeDisplay code={fundraiserCode} />}
          <header
            style={{ fontWeight: "700", paddingBottom: "20px", fontSize: 20 }}
          >
            <span style={{ fontSize: 30, paddingRight: "10px" }}>
              {fundName} -
            </span>
            <span>{activeTab?.label}</span>
          </header>
          {/* Tab Buttons (or empty border if 0 or 1 tabs) */}
          <div className="team-admin-fundraiser-tab-display-tabs">
            {tabs.length > 1 ? ( // want to show no tabs if only one tab so its not confusing cuz theres no other divisions
              tabs.map((tab) => (
                <button
                  key={tab.label}
                  onClick={() => handleTabClick(tab)}
                  style={{
                    fontWeight:
                      activeTab?.label === tab.label ? "bold" : "normal",
                  }}
                  aria-selected={activeTab?.label === tab.label}
                >
                  {tab.label}
                </button>
              ))
            ) : (
              <div className="team-admin-fundraiser-tab-display-empty-tab-placeholder"></div>
            )}
          </div>
          <div>
            <SingleAdminFundraiserComponentDisplay
              accountID={accountID}
              division={activeTab?.label || ""}
              primary_division={activeTab?.primary_division || ""}
              fundraiser_id={fundraiserID}
              primary_div_id={activeTab?.primary_id || 0}
              secondary_div_id={activeTab?.secondary_id || 0}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamAdminFundraiserTabs;
